import React, { useState, useEffect, useCallback } from 'react';
import { useVisits } from '../contexts/VisitsContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './ScheduleVisitModal.css';

const VISIT_TYPES = [
  { value: 'site_visit', label: 'Site visit' },
  { value: 'unit_viewing', label: 'Unit viewing' },
  { value: 'virtual', label: 'Virtual' },
];

const TIME_SLOTS = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

export default function ScheduleVisitModal({ isOpen, onClose, compound, onSuccess }) {
  const { createVisit } = useVisits();
  const { user, setUser } = useAuth();
  const [visitType, setVisitType] = useState('site_visit');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('10:00');
  const [notes, setNotes] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);

  const needsPhone = !user?.phone;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setVisitType('site_visit');
      setDate('');
      setTime('10:00');
      setNotes('');
      setPhone('');
      setSubmitError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (!submitting) onClose();
  }, [submitting, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, handleClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) { setSubmitError('Please select a date.'); return; }
    if (needsPhone && !phone.trim()) { setSubmitError('Please enter your phone number.'); return; }
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Save phone to profile if it was just provided
      if (needsPhone && phone.trim()) {
        const { data } = await api.put('/auth/me', { phone: phone.trim() });
        setUser(data);
      }
      const scheduled_at = new Date(`${date}T${time}:00`).toISOString();
      await createVisit({
        compound_id: compound.compound_id,
        compound_name: compound.project_name,
        location: compound.location || null,
        visit_type: visitType,
        scheduled_at,
        notes: notes.trim() || null,
      });
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong. Please try again.';
      setSubmitError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose} role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 id="modal-title">Schedule a visit</h2>
          <button type="button" className="modal-close" onClick={handleClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <p className="modal-compound-name">{compound.project_name}</p>

        {success ? (
          <div className="modal-success">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="9 12 11 14 15 10"/>
            </svg>
            <p>Visit requested!</p>
            <p className="modal-success-sub">We'll confirm your visit within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-field">
              <label className="modal-label">Visit type</label>
              <div className="visit-type-pills">
                {VISIT_TYPES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    className={`visit-type-pill${visitType === t.value ? ' selected' : ''}`}
                    onClick={() => setVisitType(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="modal-row">
              <div className="modal-field">
                <label className="modal-label" htmlFor="visit-date">Date</label>
                <input
                  id="visit-date"
                  type="date"
                  className="modal-input"
                  value={date}
                  min={todayStr()}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>
              <div className="modal-field">
                <label className="modal-label" htmlFor="visit-time">Time</label>
                <select
                  id="visit-time"
                  className="modal-input"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                >
                  {TIME_SLOTS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>

            {needsPhone && (
              <div className="modal-field">
                <label className="modal-label" htmlFor="visit-phone">
                  Phone number <span className="modal-required">*</span>
                </label>
                <input
                  id="visit-phone"
                  type="tel"
                  className="modal-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+20 10 xxxx xxxx"
                />
                <span className="modal-field-hint">So we can confirm your visit</span>
              </div>
            )}

            <div className="modal-field">
              <label className="modal-label" htmlFor="visit-notes">Notes <span className="modal-optional">(optional)</span></label>
              <textarea
                id="visit-notes"
                className="modal-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any specific questions for the sales team?"
                rows={3}
              />
            </div>

            {submitError && <p className="modal-error">{submitError}</p>}

            <button type="submit" className="modal-submit-btn" disabled={submitting}>
              {submitting ? 'Requesting…' : 'Request visit'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
